import {
  FormControl,
  FormLabel,
  Heading,
  Input,
  Skeleton,
  Stack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { TagEditor } from 'apps/TenantApp/components/tags/TagEditor';
import { AlertDialog } from 'components/dialogs/AlertDialog';
import { OverlayProps } from 'components/TaskOverlay';
import React, { PropsWithChildren, Suspense, useEffect, useState } from 'react';
import { RemirrorJSON } from 'remirror';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { useFeatureFlag } from 'utils/sessionProvider';
import { AddAnnouncementAction } from './AddAnnouncementAction';
import { PublishDialog_canCreateAnnouncement_Query } from './__generated__/PublishDialog_canCreateAnnouncement_Query.graphql';
import { PublishDialog_documentNameQuery } from './__generated__/PublishDialog_documentNameQuery.graphql';

export interface PublishDialogProps extends OverlayProps {
  nodeId: string;
  title: string;
  isPublishing: boolean;

  onPublish(announcementMessage?: RemirrorJSON, documentName?: string): void;

  spaceId?: string;
  linkIds?: string[];
}

const permissionQuery = graphql`
  query PublishDialog_canCreateAnnouncement_Query($spaceId: ID!) {
    space: node(id: $spaceId) {
      ... on Space {
        modules {
          ... on SpaceAnnouncementsModule {
            canCreateAnnouncement {
              value
            }
          }
        }
      }
    }
  }
`;

const documentNameQuery = graphql`
  query PublishDialog_documentNameQuery($id: ID!) {
    document: node(id: $id) {
      id
      ... on FolderNodeInterface {
        name
        canRename {
          value
        }
      }
    }
  }
`;

export const PublishDialog = ({
  nodeId,
  title,
  onPublish,
  isPublishing,
  isOpen,
  onClose,
  children,
  linkIds,
  spaceId,
}: PropsWithChildren<PublishDialogProps>) => {
  const [message, setMessage] = useState<RemirrorJSON>();
  const [documentName, setDocumentName] = useState<string>('');
  const isRenameTagsEnabled = useFeatureFlag('FEATURE_RENAME_TAGS_TO_LABELS');

  return (
    <AlertDialog
      data-qa="publish-dialog"
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      cancelButton={{ title: t`Cancel`, onClick: onClose }}
      actionButton={{
        title: t`Publish`,
        onClick: () => onPublish(message, documentName),
        isLoading: isPublishing,
      }}
    >
      <Stack spacing={4}>
        {children}
        {spaceId && (
          <PublishDialogAnnouncement
            spaceId={spaceId}
            linkIds={linkIds || []}
            onMessageChange={setMessage}
          />
        )}
        <Suspense fallback={<DocumentNameInputLoading />}>
          <DocumentNameInput
            nodeId={nodeId}
            setValue={setDocumentName}
            value={documentName}
          />
        </Suspense>
        <FormControl as={Stack}>
          <FormLabel>
            <Heading fontSize="xs">
              {isRenameTagsEnabled ? t`Labels` : t`Tags`}
            </Heading>
          </FormLabel>
          <TagEditor nodeId={nodeId} />
        </FormControl>
      </Stack>
    </AlertDialog>
  );
};

interface DocumentNameInputProps {
  nodeId: string;
  setValue: (name: string) => void;
  value: string;
}

const DocumentNameInputLoading = () => (
  <Stack>
    <Skeleton height={10} width="100%" />
    <Skeleton height={4} width="20%" />
  </Stack>
);

const DocumentNameInput: React.FC<DocumentNameInputProps> = ({
  nodeId,
  setValue,
  value,
}) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { document } = useLazyLoadQuery<PublishDialog_documentNameQuery>(
    documentNameQuery,
    {
      id: nodeId,
    },
  );

  useEffect(() => {
    setValue(document?.name || '');
  }, [document?.name]);

  if (!document?.canRename?.value) {
    return null;
  }

  return (
    <FormControl as={Stack}>
      <FormLabel>
        <Heading fontSize="xs">{t`Name`}</Heading>
      </FormLabel>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        // @TODO Without the setTimeout we see `onCLick` a quick select & unselect of the input.
        //       So by delaying it, we allow the select to win
        onFocus={(e) => setTimeout(() => e.target.select(), 25)}
      />
    </FormControl>
  );
};

const PublishDialogAnnouncement = ({
  spaceId,
  linkIds,
  onMessageChange,
}: {
  spaceId: string;
  linkIds: string[];
  onMessageChange: (v: RemirrorJSON) => void;
}) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { space } = useLazyLoadQuery<PublishDialog_canCreateAnnouncement_Query>(
    permissionQuery,
    { spaceId },
  );
  const canCreateAnnouncement = (space?.modules || []).find(
    (m) => !!m.canCreateAnnouncement?.value,
  );

  if (!space || !canCreateAnnouncement) {
    return null;
  }

  return (
    <AddAnnouncementAction
      spaceId={spaceId}
      linkIds={linkIds}
      onMessageChange={onMessageChange}
    />
  );
};
